import { useState } from 'react';
import Modal from '../common/Modal';
import { useTotpDisable } from '../../hooks/useTotp';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'confirm' | 'verifying' | 'success';

export default function TOTPDisableModal({ isOpen, onClose, onSuccess }: Props) {
  const [step, setStep] = useState<Step>('confirm');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');

  const disableMutation = useTotpDisable();

  const handleDisable = () => {
    if (code.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setError('');
    setStep('verifying');
    disableMutation.mutate(code, {
      onSuccess: () => {
        setStep('success');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
      },
      onError: (err) => {
        setError(err instanceof Error ? err.message : 'Invalid code');
        setStep('confirm');
      },
    });
  };

  const handleClose = () => {
    if (step !== 'verifying') {
      setStep('confirm');
      setCode('');
      setError('');
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Disable Two-Factor Authentication">
      {step === 'confirm' && (
        <div className="space-y-4">
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> Disabling two-factor authentication will make your account less secure.
            </p>
          </div>

          <p className="text-sm text-gray-600">
            Enter the 6-digit code from your authenticator app to confirm.
          </p>

          <div>
            <label htmlFor="totp-disable-code" className="block text-sm font-medium text-gray-700 mb-1">
              Verification code
            </label>
            <input
              id="totp-disable-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-center text-lg tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoComplete="one-time-code"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDisable}
              disabled={code.length !== 6}
              className="flex-1 px-4 py-2 text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed rounded-md font-medium transition-colors"
            >
              Disable 2FA
            </button>
          </div>
        </div>
      )}

      {step === 'verifying' && (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4" />
          <p className="text-gray-600">Verifying code...</p>
        </div>
      )}

      {step === 'success' && (
        <div className="flex flex-col items-center justify-center py-8">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-gray-900 font-medium">Two-factor authentication disabled</p>
        </div>
      )}
    </Modal>
  );
}
